import {
  type BilletterieOrder,
  billetterieOrderReglementLabel,
  isAttachableOrder,
} from '@cpce/shared';
import Decimal from 'decimal.js';
import { Link2 } from 'lucide-react';
import { useAttachBilletterieOrder } from '@/api/billetterie-orders/billetterie-orders.mutations';
import { Badge, BadgeColor } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmButton } from '@/components/ConfirmButton';
import { StyledLink } from '@/components/StyledLink';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { formatDecimalAsPrice } from '@/utils/string.utils';
import { customerPaymentPreset } from '../billetterie-orders.utils';

interface AttachOrderCellProps {
  order: BilletterieOrder;
  cardId: number;
}

export function AttachedOrderLink(props: { order: BilletterieOrder; participationId: number }) {
  const { order, participationId } = props;
  return (
    <>
      <StyledLink
        text={`Dossier n° ${participationId}`}
        navigateTo={{
          to: '/$commiteeSlug/prestation-participations/{-$id}',
          params: { id: participationId.toString() },
        }}
      />
      {!isAttachableOrder(order) && (
        <Badge color={BadgeColor.RED}>Annulée dans la billetterie : annulez le dossier</Badge>
      )}
    </>
  );
}

/** Books a deferred order in CSE accounting; its règlement follows when the mode resolves. */
export function AttachOrderCell(props: AttachOrderCellProps) {
  const { order, cardId } = props;
  const navigate = useCommiteeSlugNavigate();
  const { mutateAsync, isPending } = useAttachBilletterieOrder();

  if (order.attachedParticipationId !== null) {
    return (
      <div className="flex flex-col items-start gap-1">
        <AttachedOrderLink order={order} participationId={order.attachedParticipationId} />
      </div>
    );
  }
  if (!isAttachableOrder(order) || !order.checkoutCompletedAt) return <span>-</span>;

  const checkoutCompletedAt = order.checkoutCompletedAt;
  const amount = formatDecimalAsPrice(new Decimal(order.total).div(100));
  const attach = async () => {
    const { participationId, settled } = await mutateAsync({
      orderId: order.id,
      checkoutCompletedAt,
      cardId,
    });
    if (settled) return;
    navigate({
      to: '/$commiteeSlug/beneficiaries/transactions/create',
      search: {
        employeeId: cardId,
        prestationParticipationIds: [participationId],
        label: billetterieOrderReglementLabel(order),
        paymentPreset: customerPaymentPreset(order),
      },
    });
  };

  return (
    <ConfirmButton
      confirmationDescription={`La commande ${order.number ?? order.id} (${amount}) sera enregistrée comme une vente au salarié sur la prestation « Billetterie Pass CE », et son règlement avec elle quand son mode est connu.`}
      confirmationButtonLabel="Rattacher"
      onConfirm={attach}
      button={(openModal) => (
        <Button
          type="button"
          variant="outline"
          icon={Link2}
          isPending={isPending}
          onClick={(event) => {
            event.stopPropagation();
            openModal();
          }}
        >
          Rattacher
        </Button>
      )}
    />
  );
}
